// Dashboard 1 — Executive Overview. Volume cards + the delivery/engagement rate
// row (N/A rule) + conversion-type counts + 5 daily trend line charts.

import React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "../../../ui/skeleton";
import { Card, CardContent } from "../../../ui/card";
import { StatCard } from "../../../Components/admin/reporting/primitives";
import { Panel } from "../../../Components/admin/reporting/controls";
import { getEngagementOverview } from "../../../Services/adminEngagement";
import {
  CONVERSION_TYPE_LABELS,
  RESERVED_CONVERSION_TYPES,
  Rate,
  ErrorBox,
  EmptyState,
  WindowBadge,
  fmtInt,
  useEngagementData,
} from "./engagementShared";

const RateCell = ({ label, value, available, hint }) => (
  <Card className="border-slate-200/80 shadow-sm">
    <CardContent className="p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">
        <Rate value={value} available={available} hint={hint} />
      </div>
    </CardContent>
  </Card>
);

const TrendChart = ({ title, data, dataKey, color, isRate }) => (
  <Panel title={title}>
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={24} />
        <YAxis
          tick={{ fontSize: 10 }}
          width={40}
          allowDecimals={isRate}
          tickFormatter={isRate ? (v) => `${Math.round(v * 100)}%` : undefined}
        />
        <Tooltip
          formatter={(v) => (isRate ? `${(v * 100).toFixed(1)}%` : fmtInt(v))}
        />
        <Line
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  </Panel>
);

const OverviewTab = ({ range }) => {
  const { data, loading, error } = useEngagementData(
    () => getEngagementOverview(range),
    [range]
  );

  if (loading && !data) {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }
  if (error) return <ErrorBox>{error}</ErrorBox>;
  if (!data) return <EmptyState>No data available.</EmptyState>;

  const vol = data.messaging_volume || {};
  const rates = data.delivery_metrics || {};
  const conv = data.conversion_metrics || {};
  const trend = data.trend || [];

  const convEntries = Object.keys(CONVERSION_TYPE_LABELS).filter(
    (k) => conv[k] != null
  );

  return (
    <div className="space-y-6">
      <WindowBadge window={data.window} timezone={data.timezone} />

      {/* Messaging volume */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Total Sent" value={fmtInt(vol.total_messages_sent)} />
        <StatCard label="Push" value={fmtInt(vol.push_sent)} />
        <StatCard label="Email" value={fmtInt(vol.emails_sent)} />
        <StatCard label="SMS" value={fmtInt(vol.sms_sent)} />
        <StatCard label="Mobile In-App" value={fmtInt(vol.mobile_notifications_sent)} />
      </div>

      {/* Delivery & engagement rates (N/A rule) */}
      <div>
        <h2 className="mb-3 text-sm font-semibold tracking-tight">Rates</h2>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <RateCell
            label="Delivery"
            value={rates.delivery_rate}
            available={rates.delivery_rate_available}
          />
          <RateCell
            label="Open"
            value={rates.open_rate}
            available={rates.open_rate_available}
            hint="open"
          />
          <RateCell
            label="Click"
            value={rates.click_rate}
            available={rates.click_rate_available}
            hint="click"
          />
          <RateCell
            label="Session Start"
            value={rates.session_start_rate}
            available={rates.session_start_rate_available}
          />
          <RateCell
            label="Conversion"
            value={rates.conversion_rate}
            available={rates.conversion_rate_available}
          />
        </div>
      </div>

      {/* Conversion-type counts */}
      <Panel title="Conversions by type">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
          {convEntries.map((k) => {
            const reserved = RESERVED_CONVERSION_TYPES.has(k);
            return (
              <div key={k} className="flex items-baseline justify-between gap-2">
                <span
                  className={
                    reserved
                      ? "text-xs text-muted-foreground/60"
                      : "text-xs text-muted-foreground"
                  }
                >
                  {CONVERSION_TYPE_LABELS[k]}
                  {reserved ? (
                    <span className="ml-1 text-[10px] italic">(reserved)</span>
                  ) : null}
                </span>
                <span
                  className={
                    reserved
                      ? "text-sm font-medium tabular-nums text-muted-foreground/60"
                      : "text-lg font-semibold tabular-nums"
                  }
                >
                  {fmtInt(conv[k])}
                </span>
              </div>
            );
          })}
        </div>
      </Panel>

      {/* Daily trend line charts */}
      {trend.length ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <TrendChart title="Messages Sent" data={trend} dataKey="messages_sent" color="#2563eb" />
          <TrendChart title="Opens" data={trend} dataKey="opens" color="#7c3aed" />
          <TrendChart title="Clicks" data={trend} dataKey="clicks" color="#0891b2" />
          <TrendChart title="Sessions Generated" data={trend} dataKey="sessions_generated" color="#059669" />
          <TrendChart
            title="Conversion Rate"
            data={trend}
            dataKey="conversion_rate"
            color="#f59e0b"
            isRate
          />
        </div>
      ) : (
        <Panel title="Daily trend">
          <EmptyState>No trend data in this window.</EmptyState>
        </Panel>
      )}
    </div>
  );
};

export default OverviewTab;
